import type { SeriesConfig } from '@/pages/dashboard/components/EventMetricTrend/types';
import type { CSSProperties, ReactNode } from 'react';
import type { TKpiOverviewDataScope } from '../../utils/uaRoiUaMediaScopeFilters';

export const DIMENSION_DEFAULT_API =
  '/api/v1/dashboard/pc/overview_daily/dimension/metrics_trend';

/** Service trackData：与 PageContainer 合并请求上下文 */
export const DIMENSION_DEFAULT_TRACK_DATA = [
  'extraParams',
  'gameCode',
  'metrics',
  'topNum',
  'dimensionKey',
  'accumulation',
  'dimensionValueSource',
  'dimensionRowField',
  'selectorMapKey',
  'logicalFlag',
  'multiSort',
  'metricResponseKey',
  'allowedCampaignCodes',
  'allowedOsCodes',
  'allowedUaNetworkCodes',
  'dimensionSeriesOrderPriority',
  'dimensionSeriesColorMap',
] as const;

/**
 * dimension 系列 beforeRequest 共用入参结构：由 Service 聚合 filterVal + trackData + config 注入。
 * 字段以可选形式列出以便就近使用 optional-chaining，而不引入严格绑定。
 */
export interface IKpiDimensionReqData {
  filterVal?: Record<string, unknown>;
  gameCode?: string;
  metrics?: string[];
  topNum?: number;
  dimensionKey?: string;
  logicalFlag?: string;
  allowedCampaignCodes?: unknown;
  allowedOsCodes?: unknown;
  allowedUaNetworkCodes?: unknown;
  config?: Record<string, unknown> & {
    topNum?: number;
    dimensionKey?: string;
    logicalFlag?: string;
    accumulation?: unknown;
    multiSort?: unknown;
    allowedCampaignCodes?: unknown;
    allowedOsCodes?: unknown;
    allowedUaNetworkCodes?: unknown;
  };
  [key: string]: unknown;
}

/**
 * KpiEventDimensionTrendService props：外层适配组件 props。
 * 字段较多（注册表驱动），此处以宽泛接口承载；未直接消费的字段通过 rest 透传。
 */
export interface IKpiEventDimensionTrendServiceProps {
  style?: CSSProperties;
  className?: string;
  config?: Record<string, unknown>;
  children?: ReactNode;
  data?: Record<string, unknown>;
  eventData?: unknown;
  loading?: unknown;
  anomalyResult?: unknown;
  anomalyResultError?: unknown;
  anomalyResultLoading?: unknown;
  api?: string | ((data: unknown, outParame?: unknown) => void);
  dataKey?: string;
  trackData?: readonly string[];
  // beforeRequest / formatData 形态由上游运行时决定：可能是 string（用 stringToFunction 解析）或函数
  beforeRequest?: string | ((data: unknown) => unknown);
  formatData?: string | ((data: unknown, filterVal?: unknown) => unknown);
  metrics?: unknown[];
  topNum?: number;
  dimensionKey?: string;
  logicalFlag?: string;
  accumulation?: unknown;
  valueFormatRule?: string;
  selectorMapKey?: string;
  dimensionValueSource?: string;
  dimensionRowField?: string;
  multiSort?: unknown;
  metricResponseKey?: string;
  contentHeader?: ReactNode;
  dimensionSecondaryRowField?: string;
  dimensionLegendCompositeKeys?: unknown;
  mergeAnomalyIntoTrend?: boolean;
  anomalyMetricKey?: string;
  allowedCampaignCodes?: unknown;
  allowedOsCodes?: unknown;
  allowedUaNetworkCodes?: unknown;
  campaignMatchType?: string;
  campaignSelectorKey?: string;
  uaNetworkMatchType?: string[];
  uaNetworkMatchTypeNot?: string[];
  uaNetworkSelectorKey?: string;
  downloadIncludeLastWeek?: boolean;
  dimensionSeriesOrderPriority?: unknown;
  dimensionSeriesColorMap?: unknown;
  /** 在此 KPI 数据口径下隐藏整块图表（如 UA-ROI Spend 维图） */
  hideWhenKpiOverviewScope?: TKpiOverviewDataScope;
  /** 可选覆盖；默认取自 props.data.kpiOverviewDataScope（KpiOverviewScopeTabs 下发） */
  kpiOverviewDataScope?: TKpiOverviewDataScope;
  /** 与 KPI 总览 Tab「UA-ROI Campaign & UA Media Channel」口径一致时合并 custom_filters */
  applyKpiUaRoiUaMediaScope?: boolean;
}

export type TTrendBodyWithDimension = {
  dimensionSeriesConfig?: SeriesConfig[];
  dimensionValueFormatRule?: string;
};

/** EventMetricTrend 子节点 clone 前读取的 props 形状（运行时注入） */
export interface IEventMetricTrendChildStubProps {
  seriesConfig?: unknown[];
  chartConfig?: Record<string, unknown>;
}

/** dimensionLegendCompositeKeys 合法二元组 */
export type TDimensionLegendCompositeKeyPair = [string, string];

/** KpiEventDimensionTrendInner props：Service 内层，props 字段种类多，此处列出主要分发字段 */
export interface IKpiEventDimensionTrendInnerProps {
  children?: ReactNode;
  eventData?: unknown;
  loading?: unknown;
  anomalyResult?: unknown;
  anomalyResultError?: unknown;
  anomalyResultLoading?: unknown;
  mergeAnomalyIntoTrend?: boolean;
  anomalyMetricKey?: string;
  kpiOverviewDataScope?: TKpiOverviewDataScope;
  data?: Record<string, unknown> & { trendData?: Record<string, unknown>; };
  selectorMapKey?: string;
  dimensionLegendCompositeKeys?: unknown;
  forceNoData?: boolean;
  downloadIncludeLastWeek?: boolean;
  contentHeader?: ReactNode;
  [key: string]: unknown;
}

/** 外层 hook 注入 Inner 的 props（不含 Service 下发的 data） */
export interface IKpiEventDimensionTrendInnerInjectProps {
  selectorMapKey: string;
  dimensionLegendCompositeKeys: unknown;
  contentHeader: ReactNode;
  downloadIncludeLastWeek: boolean | undefined;
  eventData: unknown;
  loading: unknown;
  anomalyResult: unknown;
  anomalyResultError: unknown;
  anomalyResultLoading: unknown;
  mergeAnomalyIntoTrend: boolean | undefined;
  anomalyMetricKey: string | undefined;
  kpiOverviewDataScope: TKpiOverviewDataScope;
  forceNoData: boolean;
  children: ReactNode;
}

export interface IKpiEventDimensionTrendServiceOuterResult {
  hideBlock: boolean;
  className: string | undefined;
  style: CSSProperties | undefined;
  serviceMountKey: string;
  wrappedApi: string | ((data: unknown, outParame?: unknown) => void);
  data: Record<string, unknown>;
  dataKey: string;
  trackData: string[];
  beforeRequest: (data: IKpiDimensionReqData) => unknown;
  formatData: string | ((data: unknown, filterVal?: unknown) => unknown);
  innerProps: IKpiEventDimensionTrendInnerInjectProps;
}

/** Inner 内读取 trendData.data 上 seriesData 的断言切片 */
export type TTrendServiceDataWithSeries = { seriesData?: unknown; };

/** Inner 内 enriched seriesData 所在 body 形状 */
export type TTrendServiceBodyEnriched = {
  seriesData?: Record<string, unknown>[];
  [k: string]: unknown;
};

/** 图例中英文对（selectorMap 映射） */
export type TDimensionLegendEnCn = { enName: string; cnName: string; };

export type TDimensionLegendGetter = (
  regionDataType: string,
  code: string,
) => TDimensionLegendEnCn;
